const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    // Always serve over HTTPS — .env.local may set an http:// address for local dev
    // but Vite bakes .env.local into production builds too, so upgrade it here.
    // localhost/127.0.0.1 stay http: they have no TLS and can't be mixed content.
    const url = import.meta.env.VITE_API_URL as string;
    if (/^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?/.test(url)) return url;
    return url.replace(/^http:\/\//, 'https://');
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * localStorage that cannot throw.
 *
 * On iOS Safari `setItem` raises QuotaExceededError under storage pressure and in Private
 * Browsing, and `getItem` can throw when storage is disabled outright. An unguarded call
 * anywhere in the auth path aborts the surrounding flow mid-way — which is how a login that
 * succeeded on the server still left the browser with no session. Persisting is best-effort;
 * every caller must remain correct when it silently fails.
 */
export function safeLocalStorageSet(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function safeLocalStorageRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* nothing to clean up if storage is unavailable */
  }
}

export class ApiClient {
  setToken(token: string) {
    // COOKIE FIRST — this ordering is load-bearing, do not swap it back.
    //
    // localStorage.setItem THROWS on iOS Safari when storage is unavailable (Private
    // Browsing, or QuotaExceededError under storage pressure). When the localStorage
    // write came first, that throw propagated out of setToken and the cookie line below
    // never ran — so the token was lost ENTIRELY, the very case the cookie backup exists
    // to survive. ProtectedLayout then saw no token and bounced the user to /signin,
    // producing an endless sign-in loop in which every attempt authenticated successfully
    // server-side. Writing the resilient layer first means a localStorage failure can
    // never cost us the session.
    //
    // SameSite=Lax (not Strict) so the cookie is sent when the user arrives via an
    // external link (email, SMS, push notification) — Strict blocks all cross-site
    // top-level navigations, which is the common mobile entry point.
    document.cookie = `auth_token=${token}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax; Secure`;
    safeLocalStorageSet('auth_token', token);
  }

  clearToken() {
    // Cookie first here too: clearing must not be left half-done by a storage throw.
    document.cookie = 'auth_token=; path=/; max-age=0';
    safeLocalStorageRemove('auth_token');
  }

  /** Get token from localStorage, falling back to cookie if evicted */
  static getToken(): string | null {
    let ls: string | null = null;
    try {
      ls = localStorage.getItem('auth_token');
    } catch {
      /* storage unavailable — fall through to the cookie */
    }
    if (ls) return ls;
    // Recover from cookie
    const match = document.cookie.match(/(?:^|; )auth_token=([^;]+)/);
    if (match) {
      // Re-hydrating localStorage is an optimisation, not a requirement — the token is
      // returned whether or not this write succeeds.
      safeLocalStorageSet('auth_token', match[1]);
      return match[1];
    }
    return null;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Use getToken() so the cookie fallback is included — on mobile Safari,
    // localStorage can be evicted while the cookie survives the session.
    const token = ApiClient.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
      const error: any = new Error(errorData.error || errorData.message || 'Something went wrong. Please try again.');
      error.response = { data: errorData };
      throw error;
    }

    return response.json();
  }

  // Auth
  async login(email: string, password: string) {
    try {
      const data = await this.request<{ token: string; user: any }>('/api/auth/signin', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      this.setToken(data.token);
      return data;
    } catch (error: any) {
      // Backend returns needsRegistration:true when user has no recipe_app_access
      if (error.response?.data?.needsRegistration || error.needsSignup) {
        const redirectError: any = new Error('Please complete your registration first');
        redirectError.needsRegistration = true;
        redirectError.email = email;
        throw redirectError;
      }
      throw error;
    }
  }

  async register(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    userName: string,
    gender: string,
    birthday: string,
    age: string,
    userType: string,
    referralSource?: string
  ) {
    return this.request<{ message: string; user: any }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, firstName, lastName, userName, gender, birthday, age, userType, referralSource }),
    });
  }

  // Ingredients
  async getIngredients() { return this.request<any[]>('/api/ingredients'); }
  async createIngredient(data: any) { return this.request('/api/ingredients', { method: 'POST', body: JSON.stringify(data) }); }
  async updateIngredient(id: string, data: any) { return this.request(`/api/ingredients/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  async deleteIngredient(id: string) { return this.request(`/api/ingredients/${id}`, { method: 'DELETE' }); }

  // Materials
  async getMaterials() { return this.request<any[]>('/api/materials'); }
  async createMaterial(data: any) { return this.request('/api/materials', { method: 'POST', body: JSON.stringify(data) }); }
  async updateMaterial(id: string, data: any) { return this.request(`/api/materials/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  async deleteMaterial(id: string) { return this.request(`/api/materials/${id}`, { method: 'DELETE' }); }

  // Equipment
  async getEquipment() { return this.request<any[]>('/api/equipment'); }
  async createEquipment(data: any) { return this.request('/api/equipment', { method: 'POST', body: JSON.stringify(data) }); }
  async updateEquipment(id: string, data: any) { return this.request(`/api/equipment/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  async deleteEquipment(id: string) { return this.request(`/api/equipment/${id}`, { method: 'DELETE' }); }

  // Categories
  async getCategories() { return this.request<any[]>('/api/categories'); }
  async createCategory(data: any) { return this.request('/api/categories', { method: 'POST', body: JSON.stringify(data) }); }
  async updateCategory(id: string, data: any) { return this.request(`/api/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  async deleteCategory(id: string) { return this.request(`/api/categories/${id}`, { method: 'DELETE' }); }

  // Suppliers
  async getSuppliers() { return this.request<any[]>('/api/suppliers'); }
  async createSupplier(data: any) { return this.request('/api/suppliers', { method: 'POST', body: JSON.stringify(data) }); }
  async updateSupplier(id: string, data: any) { return this.request(`/api/suppliers/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  async deleteSupplier(id: string) { return this.request(`/api/suppliers/${id}`, { method: 'DELETE' }); }

  // Recipes
  async getRecipes() { return this.request<any[]>('/api/recipes'); }
  async createRecipe(data: any) { return this.request('/api/recipes', { method: 'POST', body: JSON.stringify(data) }); }
  async updateRecipe(id: string, data: any) { return this.request(`/api/recipes/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  async deleteRecipe(id: string) { return this.request(`/api/recipes/${id}`, { method: 'DELETE' }); }

  // Generic HTTP methods
  async get<T = any>(endpoint: string): Promise<{ data: T }> {
    return { data: await this.request<T>(endpoint) };
  }

  async post<T = any>(endpoint: string, body?: any, options?: RequestInit): Promise<{ data: T }> {
    return { data: await this.request<T>(endpoint, { method: 'POST', body: body ? JSON.stringify(body) : undefined, ...options }) };
  }

  async patch<T = any>(endpoint: string, body?: any): Promise<{ data: T }> {
    return { data: await this.request<T>(endpoint, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }) };
  }

  async delete<T = any>(endpoint: string): Promise<{ data: T }> {
    return { data: await this.request<T>(endpoint, { method: 'DELETE' }) };
  }
}

export const apiClient = new ApiClient();
